import React, { useState, Component } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, Calendar, Clock, Eye, Share2, BookOpen, Lock, User, Mail, ExternalLink, ChevronDown, ChevronUp, Facebook, Twitter, Linkedin, Copy, X, ArrowLeft as ArrowLeftIcon, ArrowRight, Instagram, Youtube, Users, MessageCircle, ChevronRight, Percent, DollarSign } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { books, getBooksByAuthor } from '../data/books';
import { BookItem } from '../components/Elze/BookItem';
import { WidgetCard } from '../components/ui/WidgetCard';
import { currentUser } from '../data/currentUser';
export function ProfilePage() {
  const {
    authorId
  } = useParams();
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  // Always use the centralized current user data
  const author = currentUser;
  // Get books by this author
  const authorBooks = getBooksByAuthor(author.id);
  // Get books with active rights transfer
  const booksWithRoyaltyOpportunities = books.filter(book => book.rightsTransfer && book.rightsTransfer.active).slice(0, 4); // Limit to 4 books for a clean display
  // Generate profile URL for sharing
  const profileUrl = window.location.origin + `/profile/${author.id}`;
  // Copy profile link to clipboard
  const copyProfileLink = () => {
    navigator.clipboard.writeText(profileUrl);
    alert('Link copied to clipboard!');
  };
  // Handler for book clicks - standardized navigation to public book page
  const handleBookClick = (bookId: string) => {
    navigate(`/book/public/${bookId}`);
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1011">
      {/* Header with transparent background, logo on left and CTA on right */}
      <div className="absolute top-0 left-0 right-0 z-20" data-id="element-1012">
        <div className="max-w-7xl mx-auto px-8 sm:px-10 lg:px-12" data-id="element-1013">
          <div className="flex justify-between items-center h-16" data-id="element-1014">
            <div className="flex items-center" data-id="element-1015">
              <Link to="/dashboard" className="flex items-center" data-id="element-1016">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jJkprGARCzfGvbLTaJLb3b/Elze_logo.svg" alt="Elze Logo" className="h-8 w-auto filter brightness-0 invert-[1]" data-id="element-1017" />
              </Link>
            </div>
            <div className="flex items-center space-x-4" data-id="element-1018">
              <div className="hidden sm:block text-white text-sm" data-id="element-1019">
                Are you an author?
              </div>
              <Link to="/dashboard" className="py-2 px-4 bg-[#C6D6AB] hover:bg-[#b8c99a] text-gray-800 text-sm font-medium rounded-md transition-colors backdrop-blur-sm" data-id="element-1020">
                Join Elze
              </Link>
              {/* Profile share button */}
              <button onClick={() => setShowShareModal(true)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur-sm" title="Share profile" data-id="element-1021">
                <Share2 size={16} data-id="element-1022" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Hero Section - Profile with blue background */}
      <div className="relative bg-[#376770] pt-16 pb-12 text-white" data-id="element-1023">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col items-center text-center relative z-10" data-id="element-1024">
          <div className="h-28 w-28 rounded-full overflow-hidden border-2 border-white/20 mb-6" data-id="element-1025">
            <img src={author.image} alt={author.name} className="h-full w-full object-cover" data-id="element-1026" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1" data-id="element-1027">{author.name}</h1>
          <div className="text-md text-gray-400 mb-4" data-id="element-1028">
            @{author.username || author.name.toLowerCase().replace(' ', '')}
          </div>
          <p className="max-w-2xl text-gray-300 mb-8" data-id="element-1029">{author.bio}</p>
          {/* Stats */}
          <div className="flex justify-center space-x-8 md:space-x-16 mb-6" data-id="element-1030">
            <div className="text-center" data-id="element-1031">
              <div className="text-3xl font-bold" data-id="element-1032">
                {author.works || authorBooks.length}
              </div>
              <div className="text-sm text-gray-400" data-id="element-1033">Total Works</div>
            </div>
            <div className="text-center" data-id="element-1034">
              <div className="text-2xl font-bold" data-id="element-1035">
                {author.followers ? author.followers.toLocaleString() : '0'}
              </div>
              <div className="text-xs text-gray-400" data-id="element-1036">Readers</div>
            </div>
            <div className="text-center" data-id="element-1037">
              <div className="text-2xl font-bold" data-id="element-1038">{author.awards || 0}</div>
              <div className="text-xs text-gray-400" data-id="element-1039">Reviews</div>
            </div>
          </div>
          {/* Social Media Links */}
          <div className="flex space-x-5" data-id="element-1040">
            {author.socialLinks?.instagram && <a href={author.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-400 transition-colors" data-id="element-1041">
                <Instagram size={20} data-id="element-1042" />
              </a>}
            {author.socialLinks?.twitter && <a href={author.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors" data-id="element-1043">
                <Twitter size={20} data-id="element-1044" />
              </a>}
            {author.socialLinks?.facebook && <a href={author.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors" data-id="element-1045">
                <Facebook size={20} data-id="element-1046" />
              </a>}
            {author.socialLinks?.youtube && <a href={author.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-1047">
                <Youtube size={20} data-id="element-1048" />
              </a>}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8" data-id="element-1049">
        {/* Works Section - Enhanced with grid display */}
        <WidgetCard title="My Works" subtitle="A collection of stories that explore the boundaries of imagination" data-id="element-1050">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 py-4" data-id="element-1051">
            {authorBooks.length > 0 ? <>
                {authorBooks.map(book => <div key={book.id} className="cursor-pointer relative" onClick={() => handleBookClick(book.id)} data-id="element-1052">
                    <BookItem cover={book.cover} title={book.title} genre={book.genre} year={book.year} id={book.id} authorName={book.author.name} authorImage={book.author.image} onClick={() => handleBookClick(book.id)} data-id="element-1053" />
                  </div>)}
                {/* Additional books from other authors as suggestions */}
                {books.filter(book => book.author.id !== author.id).slice(0, 5).map(book => <div key={book.id} className="cursor-pointer relative" onClick={() => handleBookClick(book.id)} data-id="element-1054">
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent p-2 z-10 rounded-t-md" data-id="element-1055">
                        <div className="text-xs text-white" data-id="element-1056">Recommended</div>
                      </div>
                      <BookItem cover={book.cover} title={book.title} genre={book.genre} year={book.year} id={book.id} authorName={book.author.name} authorImage={book.author.image} onClick={() => handleBookClick(book.id)} data-id="element-1057" />
                    </div>)}
              </> : <div className="col-span-full py-8 text-center" data-id="element-1058">
                <p className="text-gray-500" data-id="element-1059">No works available</p>
              </div>}
          </div>
        </WidgetCard>
        {/* Royalty Investment Opportunities Section */}
        <WidgetCard title="Royalty Investment Opportunities" subtitle="Become part of literary success by investing in royalty rights" data-id="element-1060">
          {booksWithRoyaltyOpportunities.length > 0 ? <div className="py-4" data-id="element-1061">
              <div className="overflow-x-auto scrollbar-hide pb-2" data-id="element-1062">
                <div className="flex space-x-4 w-max" data-id="element-1063">
                  {booksWithRoyaltyOpportunities.map(book => <div key={book.id} className="w-[250px] flex-shrink-0 border border-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow" onClick={() => handleBookClick(book.id)} data-id="element-1064">
                      <div className="flex items-center mb-4" data-id="element-1065">
                        <div className="w-[60px] h-[90px] flex-shrink-0 shadow-sm rounded-md overflow-hidden" data-id="element-1066">
                          <img src={book.cover} alt={book.title} className="w-full h-full object-cover" data-id="element-1067" />
                        </div>
                        <div className="ml-3 flex-1 min-w-0" data-id="element-1068">
                          <h3 className="font-medium text-gray-900 text-sm truncate" data-id="element-1069">
                            {book.title}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-1070">
                            {book.author.name}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3 mb-4" data-id="element-1071">
                        <div className="bg-gray-50 p-2 rounded" data-id="element-1072">
                          <div className="text-xs text-gray-500 mb-1" data-id="element-1073">
                            Transferred
                          </div>
                          <div className="text-sm font-medium text-gray-900" data-id="element-1074">
                            {book.rightsTransfer.rightsPercentage}%
                          </div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded" data-id="element-1075">
                          <div className="text-xs text-gray-500 mb-1" data-id="element-1076">
                            Min. inv.
                          </div>
                          <div className="text-sm font-medium text-gray-900" data-id="element-1077">
                            0.5%
                          </div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded" data-id="element-1078">
                          <div className="text-xs text-gray-500 mb-1" data-id="element-1079">
                            Total value
                          </div>
                          <div className="text-sm font-medium text-gray-900" data-id="element-1080">
                            {book.rightsTransfer.budget || '€15,000'}
                          </div>
                        </div>
                      </div>
                      <button className="w-full px-3 py-2 bg-[#376770] text-white text-xs rounded hover:bg-[#4a7c8a] transition-colors flex items-center justify-center" data-id="element-1081">
                        <Mail size={12} className="mr-1.5" data-id="element-1082" />
                        Contact Elze Team
                      </button>
                    </div>)}
                </div>
              </div>
              <div className="mt-4 text-center" data-id="element-1083">
                <button className="px-4 py-2 text-sm font-medium text-[#376770] hover:text-[#4a7c8a] transition-colors flex items-center mx-auto" data-id="element-1084">
                  View all opportunities
                  <ChevronRight size={16} className="ml-1" data-id="element-1085" />
                </button>
              </div>
            </div> : <div className="py-8 text-center" data-id="element-1086">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-1087">
                <Percent size={24} className="text-gray-400" data-id="element-1088" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1089">
                No investment opportunities at the moment
              </h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto mb-4" data-id="element-1090">
                There are currently no works available for royalty investment.
                Check back later or sign up to receive notifications.
              </p>
              <button className="px-4 py-2 bg-[#376770] text-white rounded-md hover:bg-[#4a7c8a] transition-colors text-sm" data-id="element-1091">
                Receive Notifications
              </button>
            </div>}
        </WidgetCard>
        {/* Social Media Section */}
        <WidgetCard title="From My Social" subtitle="Follow my creative journey through social media" data-id="element-1092">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1093">
            <SocialCard platform="instagram" days={2} content={`Just finished revising the fifth chapter of '${authorBooks[0]?.title || 'My new book'}'. The characters' emotions are coming to life more and more... 📝`} likes={124} comments={20} image={authorBooks[0]?.cover} data-id="element-1094" />
            <SocialCard platform="twitter" days={1} content={`Thank you all for the support on "${authorBooks[1]?.title || 'My recent book'}"! Seeing your reactions fills my heart. Creativity is contagious! ❤️`} likes={242} comments={35} data-id="element-1095" />
            <SocialCard platform="facebook" days={3} content='New review for "Midnight". Sometimes the best ideas come when you least expect them... ✨' likes={78} comments={17} data-id="element-1096" />
          </div>
          <div className="mt-4 text-center" data-id="element-1097">
            <button className="px-4 py-2 text-sm font-medium text-[#376770] hover:text-[#4a7c8a] transition-colors" data-id="element-1098">
              View all posts
            </button>
          </div>
        </WidgetCard>
      </div>
      {/* Footer with contacts */}
      <footer className="bg-[#376770] py-12 border-t border-[#2a3349]/50" data-id="element-1099">
        <div className="max-w-7xl mx-auto px-8 sm:px-10 lg:px-12" data-id="element-1100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1101">
            <div data-id="element-1102">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-1103">Contact</h3>
              <p className="text-gray-300 mb-4" data-id="element-1104">
                The platform dedicated to authors for protecting and sharing
                their works.
              </p>
              <div className="text-gray-300" data-id="element-1105">
                <div className="mb-2" data-id="element-1106">
                  {author.username || author.name.toLowerCase().replace(' ', '.')}
                  @email.com
                </div>
                <div data-id="element-1107">+39 123 456 7890</div>
              </div>
            </div>
            <div className="md:text-right" data-id="element-1108">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-1109">
                Follow Me
              </h3>
              <div className="flex space-x-4 md:justify-end" data-id="element-1110">
                {author.socialLinks?.instagram && <a href={author.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-pink-400 transition-colors" data-id="element-1111">
                    <Instagram size={20} data-id="element-1112" />
                  </a>}
                {author.socialLinks?.twitter && <a href={author.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-blue-400 transition-colors" data-id="element-1113">
                    <Twitter size={20} data-id="element-1114" />
                  </a>}
                {author.socialLinks?.facebook && <a href={author.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-blue-500 transition-colors" data-id="element-1115">
                    <Facebook size={20} data-id="element-1116" />
                  </a>}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-[#2a3349]/50 text-center text-gray-300 text-sm" data-id="element-1117">
            <div className="flex justify-center mb-2" data-id="element-1118">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jJkprGARCzfGvbLTaJLb3b/Elze_logo.svg" alt="Elze Logo" className="h-8 w-auto filter brightness-0 invert-[1]" data-id="element-1119" />
            </div>
            <p data-id="element-1120">© 2023 Elze. All rights reserved.</p>
          </div>
        </div>
      </footer>
      {/* Share Modal */}
      {showShareModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1121">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1122">
            <div className="flex items-center justify-between px-4 py-3 border-b" data-id="element-1123">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1124">
                Share Profile
              </h3>
              <button onClick={() => {
            setShowShareModal(false);
            setShowQRCode(false);
          }} className="text-gray-500 hover:text-gray-700" data-id="element-1125">
                <X size={20} data-id="element-1126" />
              </button>
            </div>
            <div className="p-4" data-id="element-1127">
              {showQRCode ? <div className="text-center" data-id="element-1128">
                  <div className="flex justify-center mb-4" data-id="element-1129">
                    <QRCodeSVG value={profileUrl} size={200} includeMargin={true} bgColor={'#ffffff'} fgColor={'#376770'} data-id="element-1130" />
                  </div>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1131">
                    Scan this QR code to share {author.name}'s profile
                  </p>
                  <button onClick={() => setShowQRCode(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors" data-id="element-1132">
                    Back to options
                  </button>
                </div> : <>
                  <div className="mb-4" data-id="element-1133">
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1134">
                      Share {author.name}'s profile on social media or copy the
                      link
                    </p>
                    <div className="flex justify-center space-x-4 mb-6" data-id="element-1135">
                      <button className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:bg-[#1877F2]/90 transition-colors" data-id="element-1136">
                        <Facebook size={20} data-id="element-1137" />
                      </button>
                      <button className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:bg-[#1DA1F2]/90 transition-colors" data-id="element-1138">
                        <Twitter size={20} data-id="element-1139" />
                      </button>
                      <button className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:bg-[#0A66C2]/90 transition-colors" data-id="element-1140">
                        <Linkedin size={20} data-id="element-1141" />
                      </button>
                    </div>
                    <div className="flex items-center" data-id="element-1142">
                      <input type="text" value={profileUrl} readOnly className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-[#376770]/20 focus:border-[#376770]" data-id="element-1143" />
                      <button onClick={copyProfileLink} className="px-3 py-2 bg-[#376770] text-white rounded-r-md text-sm hover:bg-[#4a7c8a] transition-colors" data-id="element-1144">
                        <Copy size={16} data-id="element-1145" />
                      </button>
                    </div>
                  </div>
                  <div className="text-center" data-id="element-1146">
                    <button onClick={() => setShowQRCode(true)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors" data-id="element-1147">
                      Show QR Code
                    </button>
                  </div>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}
// Social media card component
function SocialCard({
  platform,
  days,
  content,
  likes,
  comments,
  views,
  image
}) {
  const getPlatformIcon = () => {
    switch (platform) {
      case 'instagram':
        return <Instagram size={18} data-id="element-1148" />;
      case 'twitter':
        return <Twitter size={18} data-id="element-1149" />;
      case 'facebook':
        return <Facebook size={18} data-id="element-1150" />;
      case 'youtube':
        return <Youtube size={18} data-id="element-1151" />;
      default:
        return null;
    }
  };
  const getPlatformColor = () => {
    switch (platform) {
      case 'instagram':
        return 'text-pink-500 bg-pink-50';
      case 'twitter':
        return 'text-blue-400 bg-blue-50';
      case 'facebook':
        return 'text-blue-600 bg-blue-50';
      case 'youtube':
        return 'text-red-500 bg-red-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  };
  return <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-1152">
      <div className="p-4" data-id="element-1153">
        <div className="flex items-center mb-3" data-id="element-1154">
          <div className={`p-2 rounded-full ${getPlatformColor()} mr-3`} data-id="element-1155">
            {getPlatformIcon()}
          </div>
          <div data-id="element-1156">
            <div className="font-medium text-gray-900 capitalize" data-id="element-1157">
              {platform}
            </div>
            <div className="text-xs text-gray-500" data-id="element-1158">{days} days ago</div>
          </div>
        </div>
        <p className="text-gray-700 text-sm mb-4" data-id="element-1159">{content}</p>
        {image && <div className="mb-4 rounded-md overflow-hidden" data-id="element-1160">
            <img src={image} alt="" className="w-full h-40 object-cover" data-id="element-1161" />
          </div>}
        <div className="flex items-center text-xs text-gray-500" data-id="element-1162">
          {likes && <div className="flex items-center mr-4" data-id="element-1163">
              <Heart data-id="element-1164" /> <span className="ml-1" data-id="element-1165">{likes}</span>
            </div>}
          {comments && <div className="flex items-center mr-4" data-id="element-1166">
              <MessageCircle size={14} data-id="element-1167" />{' '}
              <span className="ml-1" data-id="element-1168">{comments}</span>
            </div>}
          {views && <div className="flex items-center" data-id="element-1169">
              <Eye size={14} data-id="element-1170" /> <span className="ml-1" data-id="element-1171">{views}</span>
            </div>}
        </div>
      </div>
    </div>;
}
// Additional icons for social
function Heart() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1172">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" data-id="element-1173"></path>
    </svg>;
}